import { useEffect, useState } from 'react';
import { User, sendEmailVerification } from 'firebase/auth';
import { auth } from '../lib/firebase';

export interface AuthState {
  currentUser: User | null;
  loading: boolean;
  email: string;
  anonymous: boolean;
  isVerified: boolean;
  isAuth: boolean;
  sendVerificationEmail: () => Promise<void>;
  signOut: () => Promise<void>;
}

const useAuth = (): AuthState => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const email = currentUser?.email || '';
  const anonymous = currentUser?.isAnonymous || false;
  const isVerified = currentUser?.emailVerified || false;
  const isAuth = currentUser !== null && !anonymous && email !== '';

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      setLoading(false);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (!isAuth && !loading) {
      auth.signOut();
    }
  }, [isAuth, loading, currentUser]);

  const sendVerificationEmail = async () => {
    if (currentUser && !isVerified) {
      await sendEmailVerification(currentUser);
      console.log('Verification email sent to:', currentUser.email);
    } else {
      console.warn('User is not logged in or already verified');
    }
  };

  const signOut = async () => {
    await auth.signOut();
    setCurrentUser(null);
    window.location.reload();
  };

  return {
    currentUser,
    loading,
    email,
    anonymous,
    isVerified,
    isAuth,
    sendVerificationEmail,
    signOut,
  };
};

export default useAuth;